import { useMemo } from "react";

import { ServiceDelayDurationInput } from "@/Administration/Components/Services/ServiceDelay/ServiceDelayDurationInput/ServiceDelayDurationInput";
import { ServiceDelaySelect } from "@/Administration/Components/Services/ServiceDelay/ServiceDelaySelect/ServiceDelaySelect";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IServicePublishingConfig, ServicePublishingRule } from "@/Common/Models/Services/IServicePublishingConfig";
import { TimeframeUnit } from "@/Common/Models/Timeframe";
import { cacheStore } from "@/Globals/Stores/CacheStore";

interface PublishSchedulerProps {
    value?: IServicePublishingConfig;
    onChange?: (value: IServicePublishingConfig) => void;
}

function ServiceDelay(props: PublishSchedulerProps): JSX.Element {
    const { value, onChange } = props;

    const { getText } = useTranslation();

    const hint = useMemo(
        () => {
            const adjustment = cacheStore.ClinicSettings.BookingWindowEndOfDayAdjustment;
            const match = adjustment.match(/^(-)?(?:(\d+)\.)?(\d+)[\.:](\d+)/); // format: [-][days.]hours:minutes[:seconds][.milliseconds]

            const sign = match[1];
            const day = match[2];
            const hour = match[3];
            const minute = match[4];

            const time = Number(day) > 0
                ? `${sign ?? ""}${day}.${hour}:${minute}`
                : `${sign ?? ""}${hour}:${minute}`;

            switch (value.rule) {
                case ServicePublishingRule.Immediate:
                    return getText("AvailabilityTimeRule_Immediate_PublicationHintLabel");
                case ServicePublishingRule.StartOfDay:
                    return getText("AvailabilityTimeRule_StartOfDay_PublicationHintLabel", { time });
                case ServicePublishingRule.FullDay:
                    return getText("AvailabilityTimeRule_FullDay_PublicationHintLabel", { time });
                case ServicePublishingRule.Continuously:
                    return getText("AvailabilityTimeRule_Continuously_PublicationHintLabel");
                default:
                    return null;
            }
        },
        [value.rule, getText]
    );

    const label = useMemo(
        () => {
            switch (value.rule) {
                case ServicePublishingRule.FullDay:
                    return getText("AvailabilityTimeRule_BeforeStartOfDayLabel");
                case ServicePublishingRule.Continuously:
                    return getText("AvailabilityTimeRule_BeforeAppointmentStartTimeLabel");
                default:
                    return null;
            }
        },
        [value.rule, getText]
    );

    const isHourly = useMemo(() => value.rule === ServicePublishingRule.Continuously, [value.rule]);

    const showDuration = useMemo(() => value.rule === ServicePublishingRule.FullDay || value.rule === ServicePublishingRule.Continuously, [value.rule]);

    return (
        <div>
            <ServiceDelaySelect
                hint={hint}
                value={value.rule}
                onChange={rule => {
                    const timeframe = (rule === ServicePublishingRule.FullDay || rule === ServicePublishingRule.Continuously)
                        ? { Value: 24, Unit: TimeframeUnit.Hour }
                        : null;

                    onChange?.({
                        ...value,
                        timeframe,
                        rule
                    });
                }}
            />

            {showDuration && (
                <ServiceDelayDurationInput
                    withUnit={isHourly}
                    label={label}
                    value={value.timeframe}
                    onChange={timeframe => onChange?.({
                        ...value,
                        timeframe
                    })}
                />
            )}
        </div>
    );
}

export { ServiceDelay };
